// src/pages/PendingWMSEntryPivot.jsx
import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { normalizeDateInput, toDisplayDate } from "../utils/dataFormatters";

const LOCKED_COLUMNS = [
  { key: "custCode", label: "Cust Code", width: 140 },
  { key: "retailerSku", label: "Retailer SKU", width: 160 },
  { key: "productCode", label: "Product Code", width: 160 },
  { key: "whseCode", label: "WHSE Code", width: 140 },
];

const LOCKED_LEFT_MAP = {};
let cumulativeLeft = 0;
LOCKED_COLUMNS.forEach((column) => {
  LOCKED_LEFT_MAP[column.key] = cumulativeLeft;
  cumulativeLeft += column.width;
});

function getLockedLeft(key) {
  return LOCKED_LEFT_MAP[key] || 0;
}

export default function PendingWMSEntryPivot() {
  const location = useLocation();
  const navigate = useNavigate();
  const rawRows = location.state?.rows;
  const sourceRows = useMemo(() => (Array.isArray(rawRows) ? rawRows : []), [rawRows]);

  const pivotData = useMemo(() => buildPivot(sourceRows), [sourceRows]);

  if (!sourceRows.length) {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Pending WMS Pivot - Cust/Product × MABD</h1>
            <p className="text-sm text-slate-600">Based on current filters from Pending WMS Entry. No data available.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/pos/pending-wms-entry")}
          className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        >
          Back
        </button>
      </div>
    );
  }

  const { mabdKeys, mabdLabels, pivotRows, columnTotals, grandTotal } = pivotData;

  const onExport = () => {
    const header = [...LOCKED_COLUMNS.map((col) => col.label), ...mabdLabels, "Grand Total"];
    const dataRows = pivotRows.map((row) => [
      row.custCode,
      row.retailerSku,
      row.productCode,
      row.whseCode,
      ...row.cells,
      row.rowTotal,
    ]);
    const totalsRow = ["Grand Total", "", "", "", ...columnTotals, grandTotal];
    const worksheet = XLSX.utils.aoa_to_sheet([header, ...dataRows, totalsRow]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Pending WMS Pivot");
    XLSX.writeFile(workbook, "Pending_WMS_Pivot.xlsx");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Pending WMS Pivot - Cust/Product × MABD</h1>
          <p className="text-sm text-slate-600">Based on current filters from Pending WMS Entry</p>
        </div>
        <div className="text-sm text-slate-600">
          {pivotRows.length.toLocaleString()} row combinations × {mabdLabels.length.toLocaleString()} MABD buckets
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate("/pos/pending-wms-entry")}
          className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onExport}
          className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          disabled={!pivotRows.length}
        >
          Export
        </button>
      </div>

      <div className="overflow-auto rounded border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-slate-50">
              {LOCKED_COLUMNS.map((column) => (
                <th
                  key={column.key}
                  className="border-b px-3 py-2 text-left font-semibold text-slate-700"
                  style={{ position: "sticky", left: getLockedLeft(column.key), zIndex: 3, background: "white", minWidth: column.width, width: column.width }}
                >
                  {column.label}
                </th>
              ))}
              {mabdLabels.map((label, index) => (
                <th key={mabdKeys[index]} className="border-b px-3 py-2 text-right font-semibold text-slate-700">
                  {label}
                </th>
              ))}
              <th className="border-b px-3 py-2 text-right font-semibold text-slate-700">Grand Total</th>
            </tr>
          </thead>
          <tbody>
            {pivotRows.length === 0 ? (
              <tr>
                <td colSpan={LOCKED_COLUMNS.length + mabdLabels.length + 1} className="px-3 py-6 text-center text-slate-500">
                  No pivot data available.
                </td>
              </tr>
            ) : (
              pivotRows.map((row) => (
                <tr key={`${row.custCode}||${row.retailerSku}||${row.productCode}||${row.whseCode}`}>
                  {LOCKED_COLUMNS.map((column) => (
                    <td
                      key={column.key}
                      className="border-b px-3 py-2 text-slate-700"
                      style={{ position: "sticky", left: getLockedLeft(column.key), background: "white", zIndex: 2, minWidth: column.width, width: column.width }}
                    >
                      {row[column.key] || ""}
                    </td>
                  ))}
                  {row.cells.map((value, idx) => (
                    <td key={`${row.custCode}-${mabdKeys[idx]}`} className="border-b px-3 py-2 text-right text-slate-700">
                      {value ? value.toLocaleString() : ""}
                    </td>
                  ))}
                  <td className="border-b px-3 py-2 text-right font-semibold text-slate-700">
                    {row.rowTotal ? row.rowTotal.toLocaleString() : ""}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {pivotRows.length > 0 && (
            <tfoot>
              <tr className="bg-slate-50 font-semibold text-slate-800">
                <td className="border-t px-3 py-2">Grand Total</td>
                <td className="border-t px-3 py-2" />
                <td className="border-t px-3 py-2" />
                <td className="border-t px-3 py-2" />
                {columnTotals.map((value, idx) => (
                  <td key={mabdKeys[idx]} className="border-t px-3 py-2 text-right">
                    {value ? value.toLocaleString() : ""}
                  </td>
                ))}
                <td className="border-t px-3 py-2 text-right">{grandTotal ? grandTotal.toLocaleString() : ""}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

const BLANK_MABD_KEY = "__BLANK__";

function buildPivot(rows = []) {
  if (!rows.length) {
    return {
      mabdKeys: [],
      mabdLabels: [],
      pivotRows: [],
      columnTotals: [],
      grandTotal: 0,
    };
  }

  const mabdMap = new Map(); // iso -> display label
  const aggregateMap = new Map(); // cust||sku||product -> totals

  rows.forEach((row = {}) => {
    const custCode = String(row.custCode || "").trim();
    const retailerSku = String(row.retailerSku || "").trim();
    const productCode = String(row.productCode || "").trim();
    const whseCode = String(row.whseCode || "").trim();
    const qty = parseQuantity(row.qty);
    if (!qty) return;

    const mabdSource = row?.mabdRaw ?? row?.mabd ?? "";
    let mabdKey = normalizeDateInput(mabdSource, false) || "";
    if (!mabdKey && mabdSource) {
      mabdKey = normalizeDateInput(mabdSource, true) || "";
    }
    let mabdLabel = "";
    if (mabdKey) {
      mabdLabel = toDisplayDate(mabdKey) || String(row?.mabd || mabdKey);
    } else {
      mabdKey = BLANK_MABD_KEY;
      mabdLabel = "(Blank)";
    }
    if (!mabdMap.has(mabdKey)) {
      mabdMap.set(mabdKey, mabdLabel);
    }

    const aggregateKey = `${custCode}||${retailerSku}||${productCode}||${whseCode}`;
    if (!aggregateMap.has(aggregateKey)) {
      aggregateMap.set(aggregateKey, {
        custCode,
        retailerSku,
        productCode,
        whseCode,
        totals: new Map(),
        rowTotal: 0,
      });
    }
    const target = aggregateMap.get(aggregateKey);
    target.totals.set(mabdKey, (target.totals.get(mabdKey) || 0) + qty);
    target.rowTotal += qty;
  });

  const mabdKeys = Array.from(mabdMap.keys()).sort((a, b) => {
    if (a === BLANK_MABD_KEY) return 1;
    if (b === BLANK_MABD_KEY) return -1;
    return a.localeCompare(b);
  });
  const mabdLabels = mabdKeys.map((key) => mabdMap.get(key) || key);

  const pivotRows = Array.from(aggregateMap.values())
    .filter((row) => row.rowTotal > 0)
    .sort((a, b) => {
      const custCompare = a.custCode.localeCompare(b.custCode);
      if (custCompare !== 0) return custCompare;
      const skuCompare = a.retailerSku.localeCompare(b.retailerSku);
      if (skuCompare !== 0) return skuCompare;
      const productCompare = a.productCode.localeCompare(b.productCode);
      if (productCompare !== 0) return productCompare;
      return a.whseCode.localeCompare(b.whseCode);
    })
    .map((row) => ({
      custCode: row.custCode,
      retailerSku: row.retailerSku,
      productCode: row.productCode,
      whseCode: row.whseCode,
      cells: mabdKeys.map((key) => row.totals.get(key) || 0),
      rowTotal: row.rowTotal,
    }));

  const columnTotals = mabdKeys.map((key, idx) =>
    pivotRows.reduce((sum, row) => sum + (row.cells[idx] || 0), 0)
  );
  const grandTotal = columnTotals.reduce((sum, value) => sum + value, 0);

  return {
    mabdKeys,
    mabdLabels,
    pivotRows,
    columnTotals,
    grandTotal,
  };
}

function parseQuantity(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return 0;
    const parsed = Number(trimmed.replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}
